import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, ThumbsUp, ThumbsDown } from "lucide-react";

const INSIGHTS = [
  "You start at 04:00 with a 45-min commute, so we've moved your wake time to 02:15 and set tonight's bedtime around tomorrow.",
  "Three nights in a row from Wednesday — I've front-loaded recovery on Monday and Tuesday so you arrive fresh.",
  "Your afternoon start leaves the morning free — the ideal window for training today.",
  "Roster change detected: Saturday flipped to a night shift. Your weekend plan has been rebuilt.",
];

export default function InsightGlow() {
  const [feedback, setFeedback] = useState(null);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const timer = setInterval(
      () => setIndex((i) => (i + 1) % INSIGHTS.length),
      5000
    );
    return () => clearInterval(timer);
  }, [paused]);

  return (
    <section
      data-testid="insight-section"
      className="border-t border-white/10 px-6 md:px-12 py-32 md:py-40"
    >
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
        className="max-w-3xl mx-auto text-center"
      >
        <p className="font-mono-accent text-xs sm:text-sm uppercase tracking-[0.3em] text-[#8B99AE] mb-8">
          Inside the app
        </p>
        <h2
          data-testid="insight-heading"
          className="font-serif-display font-medium text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none"
        >
          Every day starts with <span className="text-[#4ADE80]">why.</span>
        </h2>
        <p className="mt-8 text-sm sm:text-base leading-relaxed text-[#8B99AE] max-w-lg mx-auto">
          ROSTENO doesn't just show you the plan — it explains it, in plain
          language, before your feet hit the floor.
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 48, scale: 0.97 }}
        whileInView={{ opacity: 1, y: 0, scale: 1 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 1.1, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
        className="relative max-w-xl mx-auto mt-16"
      >
        <div
          aria-hidden="true"
          className="absolute -inset-10 bg-[#34D399]/15 blur-3xl rounded-full pointer-events-none"
        />
        <div
          data-testid="insight-card"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          className="relative rounded-3xl border border-[#34D399]/30 bg-[#0D1526]/90 p-8 sm:p-10"
        >
          <div className="flex items-center gap-2.5">
            <Sparkles className="w-4 h-4 text-[#4ADE80]" />
            <span className="font-mono-accent text-xs font-bold tracking-[0.25em] text-[#4ADE80]">
              ROSTENO INSIGHT
            </span>
          </div>
          <div className="mt-5 min-h-[5.5rem] sm:min-h-[4.5rem]">
            <AnimatePresence mode="wait">
              <motion.p
                key={index}
                data-testid="insight-text"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                className="text-base sm:text-lg leading-relaxed text-white/90"
              >
                {INSIGHTS[index]}
              </motion.p>
            </AnimatePresence>
          </div>
          <div className="mt-7 flex items-center gap-5 text-sm text-[#8B99AE]">
            {feedback ? (
              <span
                data-testid="insight-feedback-thanks"
                className="font-mono-accent text-xs uppercase tracking-[0.2em] text-[#4ADE80]"
              >
                Thanks — noted.
              </span>
            ) : (
              <>
                <span>Was this helpful?</span>
                <button
                  data-testid="insight-helpful-yes"
                  onClick={() => setFeedback("yes")}
                  aria-label="Yes, helpful"
                  className="transition-colors duration-300 hover:text-[#4ADE80]"
                >
                  <ThumbsUp className="w-4 h-4" />
                </button>
                <button
                  data-testid="insight-helpful-no"
                  onClick={() => setFeedback("no")}
                  aria-label="Not helpful"
                  className="transition-colors duration-300 hover:text-[#4ADE80]"
                >
                  <ThumbsDown className="w-4 h-4" />
                </button>
              </>
            )}
          </div>
        </div>
      </motion.div>
    </section>
  );
}
